import { LoadingSkeleton, AlertBanner } from '@/shared/ui/components';
import { clockIcon } from '@/shared/ui/assets';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import './shippingRate.scss';

/**
 * Renders the Shipping rate panel of the create-shipping-label page for the currently selected carrier rate. Wraps the panel in a loading skeleton while rates are being fetched, and shows a rate-unavailable message once the page has loaded but required shipment information is still missing, otherwise the rate row with its carrier, estimated days and price.
 *
 * @param props - Panel props carrying selectedRate, isLoadedData, isLoadingRates and isMissingData.
 * @returns The shipping rate panel markup.
 */
const ShippingRate = (props: any) => {
  /**
   * Renders the estimated transit time of a shipping rate next to a clock icon. Prints 'N days' or the singular 'N day' depending on rate.estimatedDays, and nothing when that value is nil.
   *
   * @param rate - Rate whose estimatedDays is displayed.
   * @returns The estimated-day markup.
   */
  const estimatedDayRenderer = (rate: any) => (
    <div className="day">
      <img alt="icon time" src={clockIcon} />
      {!_.isNil(rate.estimatedDays)
        ? rate.estimatedDays > 1
          ? `${rate.estimatedDays} days`
          : `${rate.estimatedDays} day`
        : null}
    </div>
  );

  /**
   * Renders the body of the shipping rate panel across its loading, error and loaded states. Shows three skeleton blocks until isLoadedData, a warning AlertBanner when selectedRate is empty, otherwise the carrier and service level, the estimated days, and the amount formatted to two decimals.
   *
   * @returns The rate panel body markup.
   */
  const renderList = () => {
    if (!props.isLoadedData) {
      return (
        <div className="group-skeleton">
          <Skeleton height={80} />
          <Skeleton height={80} />
          <Skeleton height={80} />
        </div>
      );
    }

    if (_.isEmpty(props.selectedRate)) {
      return (
        <AlertBanner
          description={t`Something went wrong please check your information and try again.`}
          alertType="warning"
        />
      );
    }

    return (
      <div className="shipping-rate-item">
        <div className="shipping-rate-item__left-container">
          <div className="carrier">
            {`${props.selectedRate.carrier} ${props.selectedRate.serviceLevelName}`}
          </div>
          {estimatedDayRenderer(props.selectedRate)}
        </div>
        <div className="shipping-rate-item__right-container">
          {`$${parseFloat(props.selectedRate.amount).toFixed(2)}`}
        </div>
        <div />
      </div>
    );
  };

  return (
    <div className="shipping-rate">
      <div className="shipping-rate__title-container">
        <span className="title">{t`Shipping rate`}</span>
      </div>
      <div className="shipping-rate__body-container">
        <LoadingSkeleton isLoading={props.isLoadingRates}>
          {props.isMissingData && props.isLoadedData ? (
            <div className="message-container">
              <div className="message">
                {t`Shipping rate is unavailable due to missing some required information.`}
              </div>
            </div>
          ) : (
            renderList()
          )}
        </LoadingSkeleton>
      </div>
    </div>
  );
};

export default ShippingRate;
